import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Check,
  Circle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import CommonButton from '../../common/components/CommonButton';
import CommonFormField from '../../common/components/CommonFormField';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { validateEmail } from '../../common/const';

const LoginForm = ({ onSubmit, loading }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'rememberMe') {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      const error = validateField(name, value);
      if (!error) {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      } else {
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    onSubmit(formData);
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
      }}
    >
      {loading && (
        <LoadingSpinner />
      )}
      <Box sx={{
        width: '100%',
        maxWidth: {
          sm: '400px',   // Small screens
          md: '400px',
          lg: '500px'
        }
      }}>
        {/* Header */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px' }}>
            Welcome To
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, fontSize: '24px' }}>
            Dream College Path
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 1 }}>
            <CommonFormField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Please enter your email"
              error={errors.email}
            />
          </Box>

          {/* Password Field */}
          <Box sx={{ mb: 1 }}>
            <CommonFormField
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="•••••••••"
              error={errors.password}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(prev => !prev)}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              mb: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  color="secondary"
                  size="small"
                  icon={<Circle sx={{ fontSize: 20, color: theme.palette.background.light }} />}
                  checkedIcon={
                    <Box sx={{ background: theme.palette.secondary.main, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                      <Check sx={{ color: 'white', fontSize: 12 }} />
                    </Box>
                  }
                  sx={{
                    padding: '4px',
                    '& .MuiSvgIcon-root': {
                      borderRadius: '50%',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '14px' }}>
                  Remember this Device
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>
          <CommonButton
            label="Sign In"
            size="large"
            type="submit"
            disabled={loading}
          />
        </form>
      </Box>
    </Box>
  );
};

export default LoginForm;